import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { dataHora } from '../formato';
import { Aviso, Dialogo, ErroDaApi, Selo, abrirProtegido } from '../ui';
import { NomeDaAmostra, nomeDaAmostra } from '../NomeDaAmostra';

/**
 * As etiquetas pela OS (onda 287, RN-232).
 *
 * «Avalie o melhor local para impressão das etiquetas das amostras.
 *  Acredito que tenha que ser em Ordem de Serviço» → «Desenha em canvas
 *  antes» → «Aprovo, pode fazer» — Humberto, 06/10/2026.
 *
 * A etiqueta traz a OS e os ensaios em destaque, e as duas coisas costumam
 * chegar DEPOIS do recebimento. O recebimento continua imprimindo (o QR
 * não pode esperar), mas o lugar principal passa a ser a OS: ela sabe,
 * pelo registro de cada impressão, qual etiqueta ficou velha.
 *
 * Aqui mora o que as telas dividem: a situação de cada amostra
 * (`/amostras/etiquetas/situacao`), o selo, a janela «Imprimir etiquetas»,
 * a faixa do celular, a seção da ficha e o atalho depois da Programação.
 */

export type SituacaoDaEtiqueta = 'em_dia' | 'reimprimir' | 'sem_registro';

export interface EtiquetaDaAmostra {
  amostra_id: string;
  codigo: string;
  identificacao: string | null;
  situacao: SituacaoDaEtiqueta;
  /** O que mudou desde a última impressão, em frase curta. */
  mudou: string[];
  ensaios_novos: string[];
  ultima: { em: string; por: string | null; origem: string; formato: string; modelo: string } | null;
}

export interface EtiquetasDaOs {
  modelo: string;
  resumo: { reimprimir: number; em_dia: number; sem_registro: number };
  amostras: EtiquetaDaAmostra[];
}

export type OrigemDaImpressao = 'os' | 'recebimento' | 'programacao' | 'ficha' | 'lista';

const ONDE: Record<string, string> = {
  os: 'pela OS',
  recebimento: 'no recebimento',
  programacao: 'depois da Programação',
  ficha: 'pela ficha da amostra',
  lista: 'pela lista de amostras',
};

/** A situação das etiquetas das amostras de uma OS (ou de algumas amostras). */
export function useEtiquetas(alvo: { os?: string | null; ids?: string[] }) {
  const [dados, setDados] = useState<EtiquetasDaOs | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const chave = alvo.os ? `os=${alvo.os}` : alvo.ids?.length ? `ids=${alvo.ids.join(',')}` : '';
  const recarregar = useCallback(async () => {
    if (!chave) { setDados(null); return; }
    try {
      setDados(await api<EtiquetasDaOs>(`/amostras/etiquetas/situacao?${chave}`));
      setErro(null);
    } catch (e) { setErro(e); }
  }, [chave]);
  useEffect(() => { void recarregar(); }, [recarregar]);
  const porAmostra = useMemo(
    () => new Map((dados?.amostras ?? []).map((a) => [a.amostra_id, a])), [dados]);
  return { dados, erro, recarregar, porAmostra };
}

/** Manda imprimir: o PDF do rolo ou a folha A4 — e a impressão fica registrada. */
export async function imprimirEtiquetas(p: {
  ids?: string[]; os?: string; soAsQueMudaram?: boolean;
  formato: 'rolo' | 'a4'; posicao?: number; origem: OrigemDaImpressao;
}): Promise<void> {
  const q = new URLSearchParams();
  if (p.ids?.length) q.set('ids', p.ids.join(','));
  if (p.os) q.set('os', p.os);
  if (p.soAsQueMudaram) q.set('situacao', 'reimprimir');
  if (p.formato === 'rolo') { q.set('formato', 'rolo'); q.set('pdf', '1'); }
  if (p.formato === 'a4' && p.posicao && p.posicao > 1) q.set('deslocamento', String(p.posicao - 1));
  q.set('registrar', p.origem);
  await abrirProtegido(`/amostras/etiquetas?${q.toString()}`,
    p.formato === 'rolo' ? 'etiquetas-100x50.pdf' : 'etiquetas-a4.html');
}

/** O selo da etiqueta — com palavra e símbolo, nunca só cor. */
export function SeloDaEtiqueta({ e }: { e?: EtiquetaDaAmostra | null }) {
  if (!e) return <span className="nada">—</span>;
  /* O símbolo (● ▲) é do próprio selo — aqui só a palavra. */
  if (e.situacao === 'em_dia') return <Selo tom="ok">Em dia</Selo>;
  if (e.situacao === 'reimprimir') {
    return <Selo tom="atencao" titulo={e.mudou.join(' · ')}>Reimprimir</Selo>;
  }
  return <Selo tom="neutro" titulo="O sistema ainda não registrou nenhuma impressão desta etiqueta">Sem registro</Selo>;
}

/** A célula «Etiqueta» da tabela de amostras da OS. */
export function CelulaDaEtiqueta({ e }: { e?: EtiquetaDaAmostra | null }) {
  const sub = !e ? null
    : e.situacao === 'reimprimir' ? e.mudou.join(' · ')
      : e.ultima ? `${dataHora(e.ultima.em)} · ${ONDE[e.ultima.origem] ?? e.ultima.origem}`
        : null;
  return (
    <span className="etq-celula">
      <SeloDaEtiqueta e={e} />
      {sub && <span className="sub">{sub}</span>}
    </span>
  );
}

/**
 * «Imprimir etiquetas» — a janela da OS (canvas «Etiquetas», quadro «OS ›
 * Amostras»). Três escolhas — as que mudaram, as marcadas, todas as
 * recebidas —, o formato (rolo 100 × 50 da Zebra, ou a folha A4 de 12
 * começando na posição da folha já usada), o porquê de cada uma e a prévia
 * da primeira etiqueta, do mesmo serviço que imprime.
 */
export function JanelaEtiquetas({
  aberto, osId, osNumero, etiquetas, marcadas, fixas, origem = 'os', aoFechar, aoImpresso,
}: {
  aberto: boolean;
  osId: string;
  osNumero?: string | null;
  etiquetas: EtiquetasDaOs | null;
  /** As linhas marcadas na tabela. */
  marcadas: string[];
  /** Uma lista já escolhida por quem abriu (o atalho da Programação). */
  fixas?: { ids: string[]; titulo: string };
  origem?: OrigemDaImpressao;
  aoFechar(): void;
  aoImpresso(): void;
}) {
  const todas = etiquetas?.amostras ?? [];
  const mudaram = todas.filter((a) => a.situacao === 'reimprimir');
  type Escolha = 'fixas' | 'mudaram' | 'marcadas' | 'todas';
  const inicial: Escolha = fixas ? 'fixas'
    : mudaram.length ? 'mudaram' : marcadas.length ? 'marcadas' : 'todas';
  const [escolha, setEscolha] = useState<Escolha>(inicial);
  const [formato, setFormato] = useState<'rolo' | 'a4'>('rolo');
  const [posicao, setPosicao] = useState(1);
  const [previa, setPrevia] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<unknown>(null);

  /* Reabrir a janela recomeça pela escolha que faz sentido agora. */
  useEffect(() => { if (aberto) { setEscolha(inicial); setErro(null); } }, [aberto]); // eslint-disable-line react-hooks/exhaustive-deps

  const ids = escolha === 'fixas' ? fixas?.ids ?? []
    : escolha === 'mudaram' ? mudaram.map((a) => a.amostra_id)
      : escolha === 'marcadas' ? marcadas
        : todas.map((a) => a.amostra_id);
  const primeira = ids[0] ?? null;

  useEffect(() => {
    if (!aberto || !primeira) { setPrevia(null); return; }
    let vivo = true;
    void api<string>(`/amostras/etiquetas?ids=${primeira}&formato=rolo`, { cru: true })
      .then((h) => { if (vivo) setPrevia(h); })
      .catch(() => { if (vivo) setPrevia(null); });
    return () => { vivo = false; };
  }, [aberto, primeira]);

  const porId = new Map(todas.map((a) => [a.amostra_id, a]));
  const motivos = ids.map((id) => porId.get(id)).filter((a): a is EtiquetaDaAmostra => !!a && a.situacao === 'reimprimir');

  const gerar = async () => {
    setEnviando(true); setErro(null);
    try {
      await imprimirEtiquetas(escolha === 'mudaram'
        ? { os: osId, soAsQueMudaram: true, formato, posicao, origem }
        : escolha === 'todas'
          ? { os: osId, formato, posicao, origem }
          : { ids, formato, posicao, origem });
      aoImpresso();
      aoFechar();
    } catch (e) { setErro(e); } finally { setEnviando(false); }
  };

  const opcao = (valor: Escolha, titulo: string, sub: string, n: number) => (
    <label className={`etq-opcao${escolha === valor ? ' marcada' : ''}${n === 0 ? ' desligada' : ''}`}>
      <input type="radio" name="etq-escolha" value={valor} checked={escolha === valor}
        disabled={n === 0} onChange={() => setEscolha(valor)} />
      <span><b>{titulo}</b><span className="sub">{sub}</span></span>
    </label>
  );
  const lista = (xs: string[]) => xs.slice(0, 3).join(', ') + (xs.length > 3 ? ` e mais ${xs.length - 3}` : '');
  // 303 (RN-248): as amostras são ditas pelo nome do cliente, com o código.
  const codigos = (xs: string[]) => xs
    .map((id) => { const a = porId.get(id); return a ? nomeDaAmostra(a) : ''; })
    .filter(Boolean);

  return (
    <Dialogo aberto={aberto} larga titulo="Imprimir etiquetas"
      rotulo={osNumero ? `OS ${osNumero}` : undefined} aoFechar={aoFechar}>
      <div className="etq-janela">
        <div className="etq-escolhas">
          <p className="rot">O que imprimir</p>
          {fixas && opcao('fixas', fixas.titulo, lista(codigos(fixas.ids)), fixas.ids.length)}
          {opcao('mudaram',
            mudaram.length === 0 ? 'Nenhuma mudou desde a última impressão'
              : mudaram.length === 1 ? 'A que mudou desde a última impressão'
                : `As ${mudaram.length} que mudaram desde a última impressão`,
            'OS, cliente, identificação ou ensaios diferentes do que saiu na etiqueta', mudaram.length)}
          {opcao('marcadas',
            marcadas.length === 0 ? 'Nenhuma marcada na tabela'
              : marcadas.length === 1 ? 'A marcada na tabela' : `As ${marcadas.length} marcadas na tabela`,
            marcadas.length ? lista(codigos(marcadas)) : 'marque as linhas na tabela da aba Amostras', marcadas.length)}
          {opcao('todas',
            todas.length === 1 ? 'A única recebida' : `Todas as ${todas.length} recebidas`,
            'na ordem dos lotes — as por chegar ficam de fora', todas.length)}

          <p className="rot">Formato</p>
          <div className="etq-formato" role="group" aria-label="Formato">
            <button type="button" aria-pressed={formato === 'rolo'}
              className={formato === 'rolo' ? 'ativo' : ''} onClick={() => setFormato('rolo')}>
              Rolo 100 × 50 mm · Zebra
            </button>
            <button type="button" aria-pressed={formato === 'a4'}
              className={formato === 'a4' ? 'ativo' : ''} onClick={() => setFormato('a4')}>
              Folha A4 · 12 por folha
            </button>
          </div>
          {formato === 'a4' && (
            <label className="etq-posicao">
              Começar na posição
              <input type="number" min={1} max={12} value={posicao}
                onChange={(e) => setPosicao(Math.min(12, Math.max(1, Number(e.target.value) || 1)))} />
              <span className="sub">da folha já usada (1 a 12)</span>
            </label>
          )}

          {motivos.length > 0 && (
            <>
              <p className="rot">Por que estas</p>
              <ul className="etq-por-que">
                {motivos.slice(0, 8).map((a) => (
                  <li key={a.amostra_id}>
                    <NomeDaAmostra amostra={a} emLinha /> {a.mudou.join(' · ')}
                  </li>
                ))}
                {motivos.length > 8 && <li className="sub">e mais {motivos.length - 8}</li>}
              </ul>
            </>
          )}
          <p className="sub">
            Cada impressão fica registrada — quem, quando, o formato e o que saiu
            na etiqueta. É o que permite dizer, depois, qual etiqueta ficou velha.
          </p>
        </div>
        <div className="etq-previa">
          <p className="rot">Prévia{primeira ? ` · ${(porId.get(primeira) ? nomeDaAmostra(porId.get(primeira) as EtiquetaDaAmostra) : '')}` : ''}</p>
          {previa
            ? <iframe title="Prévia da etiqueta" srcDoc={previa} className="etq-quadro" />
            : <div className="etq-quadro vazio sub">{primeira ? 'Carregando a prévia…' : 'Nada escolhido.'}</div>}
          <p className="sub">A etiqueta nova cobre a velha: o QR é o mesmo.</p>
        </div>
      </div>
      <ErroDaApi erro={erro} />
      <div className="rodape-dialogo">
        <button type="button" className="botao secundario" onClick={aoFechar} disabled={enviando}>Cancelar</button>
        <button type="button" className="botao" disabled={!ids.length || enviando} onClick={() => { void gerar(); }}>
          {enviando ? 'Gerando…'
            : formato === 'rolo' ? `Gerar PDF · ${ids.length} etiqueta(s)`
              : `Abrir a folha A4 · ${ids.length} etiqueta(s)`}
        </button>
      </div>
    </Dialogo>
  );
}

/** O botão «Etiquetas» da barra da tabela, com quantas estão para reimprimir. */
export function BotaoEtiquetas({ etiquetas, aoAbrir }: {
  etiquetas: EtiquetasDaOs | null; aoAbrir(): void;
}) {
  const n = etiquetas?.resumo.reimprimir ?? 0;
  return (
    <button type="button" className="botao secundario etq-botao" onClick={aoAbrir}
      disabled={!etiquetas || etiquetas.amostras.length === 0}>
      Etiquetas
      {n > 0 && <span className="etq-contagem">{n} a reimprimir</span>}
    </button>
  );
}

/** No celular: a faixa «N etiquetas para reimprimir» no topo da aba Amostras. */
export function FaixaDeEtiquetas({ osId, etiquetas, aoImpresso }: {
  osId: string; etiquetas: EtiquetasDaOs | null; aoImpresso(): void;
}) {
  const [erro, setErro] = useState<unknown>(null);
  const n = etiquetas?.resumo.reimprimir ?? 0;
  if (!n) return null;
  return (
    <div className="etq-faixa" role="status">
      <b>▲ {n === 1 ? '1 etiqueta para reimprimir' : `${n} etiquetas para reimprimir`}</b>
      <button type="button" className="botao" onClick={() => {
        setErro(null);
        void imprimirEtiquetas({ os: osId, soAsQueMudaram: true, formato: 'rolo', origem: 'os' })
          .then(aoImpresso).catch(setErro);
      }}>
        {n === 1 ? 'Imprimir · 100 × 50 mm' : `Imprimir as ${n} · 100 × 50 mm`}
      </button>
      <span className="sub">
        Abre o PDF: o celular manda para a Zebra pela rede, ou guarde e imprima no PC da bancada.
      </span>
      <ErroDaApi erro={erro} />
    </div>
  );
}

/** A seção «Etiqueta» da ficha da amostra: a situação e «Reimprimir etiqueta». */
export function EtiquetaNaFicha({ e, amostraId, origem = 'ficha', aoImpresso }: {
  e?: EtiquetaDaAmostra | null; amostraId: string; origem?: OrigemDaImpressao; aoImpresso(): void;
}) {
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  return (
    <section className="etq-ficha" aria-label="Etiqueta">
      <p className="rot">Etiqueta</p>
      <SeloDaEtiqueta e={e} />
      {e?.ultima && (
        <p className="etq-ficha-texto">
          Impressa em {dataHora(e.ultima.em)}{e.ultima.por ? ` por ${e.ultima.por}` : ''},{' '}
          {ONDE[e.ultima.origem] ?? e.ultima.origem}
          {e.situacao === 'reimprimir' && e.mudou.length > 0 && <> — depois disso, {e.mudou.join(' · ')}</>}.
        </p>
      )}
      {e && !e.ultima && (
        <p className="etq-ficha-texto sub">O sistema ainda não registrou nenhuma impressão desta etiqueta.</p>
      )}
      <button type="button" className="botao secundario" disabled={enviando} onClick={() => {
        setEnviando(true); setErro(null);
        void imprimirEtiquetas({ ids: [amostraId], formato: 'rolo', origem })
          .then(aoImpresso).catch(setErro).finally(() => setEnviando(false));
      }}>
        {enviando ? 'Gerando…' : e?.ultima ? 'Reimprimir etiqueta' : 'Imprimir etiqueta'}
      </button>
      <span className="sub">Também para a etiqueta que rasgou ou molhou: imprime só esta, no rolo 100 × 50 mm.</span>
      <ErroDaApi erro={erro} />
    </section>
  );
}

/**
 * Depois de confirmar a Programação: as amostras que ganharam ensaio e cuja
 * etiqueta impressa não os tem — «Imprimir as N etiquetas» abre a mesma
 * janela, já com elas escolhidas.
 */
export function EtiquetasDepoisDaProgramacao({ osId, osNumero, vez }: {
  osId: string; osNumero?: string | null; vez: number;
}) {
  const { dados, recarregar } = useEtiquetas({ os: vez > 0 ? osId : null });
  const [fechado, setFechado] = useState(false);
  const [janela, setJanela] = useState(false);
  useEffect(() => { setFechado(false); if (vez > 0) void recarregar(); }, [vez]); // eslint-disable-line react-hooks/exhaustive-deps
  const ganharam = (dados?.amostras ?? [])
    .filter((a) => a.situacao === 'reimprimir' && a.ensaios_novos.length > 0);
  if (!vez || fechado || !ganharam.length) return null;
  const n = ganharam.length;
  return (
    <div className="etq-atalho">
      <Aviso tipo="ok" titulo={n === 1 ? '1 amostra ganhou ensaio' : `${n} amostras ganharam ensaio`}>
        <p style={{ margin: '0 0 .5rem' }}>
          {ganharam.slice(0, 4).map((a) => `${nomeDaAmostra(a)} + ${a.ensaios_novos.join(' ')}`).join(' · ')}
          {n > 4 ? ` e mais ${n - 4}` : ''}. {n === 1 ? 'A etiqueta dela saiu' : 'As etiquetas delas saíram'} sem esses ensaios.
        </p>
        <div className="etq-atalho-botoes">
          <button type="button" className="botao" onClick={() => setJanela(true)}>
            {n === 1 ? 'Imprimir a etiqueta' : `Imprimir as ${n} etiquetas`}
          </button>
          <button type="button" className="botao secundario" onClick={() => setFechado(true)}>Agora não</button>
        </div>
      </Aviso>
      <JanelaEtiquetas aberto={janela} osId={osId} osNumero={osNumero} etiquetas={dados}
        marcadas={[]} origem="programacao"
        fixas={{ ids: ganharam.map((a) => a.amostra_id),
          titulo: n === 1 ? 'A que ganhou ensaio na Programação' : `As ${n} que ganharam ensaio na Programação` }}
        aoFechar={() => setJanela(false)}
        aoImpresso={() => { void recarregar(); }} />
    </div>
  );
}
